/**
 * Secure Storage Utility - Minimal surgical replacement for localStorage
 *
 * Uses the most secure storage available per environment:
 * - Extension: chrome.storage.local (encrypted by browser)
 * - Tauri: Secure keychain storage
 * - VSCode: Extension secure storage
 * - Web: sessionStorage (memory-only, cleared on tab close)
 */

// Type definitions for browser APIs
interface ChromeStorage {
  local: {
    set(items: Record<string, string>): Promise<void>;
    get(keys: string[]): Promise<Record<string, string>>;
    remove(keys: string[]): Promise<void>;
    clear(): Promise<void>;
  };
}

interface ChromeRuntime {
  sendMessage?: (message: unknown) => Promise<unknown>;
  onMessage?: {
    addListener: (
      callback: (
        message: unknown,
        sender: unknown,
        sendResponse: (response?: unknown) => void,
      ) => void,
    ) => void;
  };
  getURL?: (path: string) => string;
  id?: string;
}

interface TauriInternals {
  invoke: (cmd: string, args: Record<string, unknown>) => Promise<unknown>;
}

interface VSCodeAPI {
  postMessage: (message: unknown) => void;
}

interface ExtendedWindow extends Window {
  __IVE_EXTENSION_MODE__?: boolean;
  __TAURI__?: unknown;
  isTauri?: boolean;
  __TAURI_METADATA__?: unknown;
  __TAURI_INTERNALS__?: TauriInternals;
  chrome?: {
    storage?: ChromeStorage;
    runtime?: ChromeRuntime;
  };
  acquireVsCodeApi?: () => VSCodeAPI;
}

interface SecureStorageInterface {
  setItem(key: string, value: string): Promise<void>;
  getItem(key: string): Promise<string | null>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}

class WebSecureStorage implements SecureStorageInterface {
  async setItem(key: string, value: string): Promise<void> {
    // Use sessionStorage instead of localStorage - cleared on tab close
    sessionStorage.setItem(key, value);
  }

  async getItem(key: string): Promise<string | null> {
    return sessionStorage.getItem(key);
  }

  async removeItem(key: string): Promise<void> {
    sessionStorage.removeItem(key);
  }

  async clear(): Promise<void> {
    sessionStorage.clear();
  }
}

class ExtensionSecureStorage implements SecureStorageInterface {
  async setItem(key: string, value: string): Promise<void> {
    const chrome = (window as ExtendedWindow).chrome;
    if (chrome?.storage?.local) {
      await chrome.storage.local.set({ [key]: value });
    } else {
      // Fallback to sessionStorage
      sessionStorage.setItem(key, value);
    }
  }

  async getItem(key: string): Promise<string | null> {
    const chrome = (window as ExtendedWindow).chrome;
    if (chrome?.storage?.local) {
      const result = await chrome.storage.local.get([key]);
      return result[key] || null;
    } else {
      return sessionStorage.getItem(key);
    }
  }

  async removeItem(key: string): Promise<void> {
    const chrome = (window as ExtendedWindow).chrome;
    if (chrome?.storage?.local) {
      await chrome.storage.local.remove([key]);
    } else {
      sessionStorage.removeItem(key);
    }
  }

  async clear(): Promise<void> {
    const chrome = (window as ExtendedWindow).chrome;
    if (chrome?.storage?.local) {
      await chrome.storage.local.clear();
    } else {
      sessionStorage.clear();
    }
  }
}

class TauriSecureStorage implements SecureStorageInterface {
  async setItem(key: string, value: string): Promise<void> {
    const tauri = (window as ExtendedWindow).__TAURI_INTERNALS__;
    if (tauri?.invoke) {
      try {
        await tauri.invoke("store_secure", { key, value });
      } catch {
        // Fallback to sessionStorage
        sessionStorage.setItem(key, value);
      }
    } else {
      sessionStorage.setItem(key, value);
    }
  }

  async getItem(key: string): Promise<string | null> {
    const tauri = (window as ExtendedWindow).__TAURI_INTERNALS__;
    if (tauri?.invoke) {
      try {
        return (await tauri.invoke("get_secure", { key })) as string | null;
      } catch {
        return sessionStorage.getItem(key);
      }
    } else {
      return sessionStorage.getItem(key);
    }
  }

  async removeItem(key: string): Promise<void> {
    const tauri = (window as ExtendedWindow).__TAURI_INTERNALS__;
    if (tauri?.invoke) {
      try {
        await tauri.invoke("remove_secure", { key });
      } catch {
        sessionStorage.removeItem(key);
      }
    } else {
      sessionStorage.removeItem(key);
    }
  }

  async clear(): Promise<void> {
    const tauri = (window as ExtendedWindow).__TAURI_INTERNALS__;
    if (tauri?.invoke) {
      try {
        await tauri.invoke("clear_secure", {});
      } catch {
        sessionStorage.clear();
      }
    } else {
      sessionStorage.clear();
    }
  }
}

class VSCodeSecureStorage implements SecureStorageInterface {
  async setItem(key: string, value: string): Promise<void> {
    const vscode = (window as ExtendedWindow).acquireVsCodeApi?.();
    if (vscode?.postMessage) {
      vscode.postMessage({
        type: "store_secure",
        key,
        value,
      });
    } else {
      sessionStorage.setItem(key, value);
    }
  }

  async getItem(key: string): Promise<string | null> {
    // VSCode storage requires async messaging - for now use sessionStorage
    return sessionStorage.getItem(key);
  }

  async removeItem(key: string): Promise<void> {
    const vscode = (window as ExtendedWindow).acquireVsCodeApi?.();
    if (vscode?.postMessage) {
      vscode.postMessage({
        type: "remove_secure",
        key,
      });
    } else {
      sessionStorage.removeItem(key);
    }
  }

  async clear(): Promise<void> {
    sessionStorage.clear();
  }
}

// Environment detection and storage selection
export function createSecureStorage(): SecureStorageInterface {
  const win = window as ExtendedWindow;

  // Extension mode
  if (win.__IVE_EXTENSION_MODE__ && win.chrome?.storage?.local) {
    return new ExtensionSecureStorage();
  }

  // Tauri mode
  if (win.__TAURI__ || win.isTauri || win.__TAURI_METADATA__) {
    return new TauriSecureStorage();
  }

  // VSCode mode
  if (win.acquireVsCodeApi) {
    return new VSCodeSecureStorage();
  }

  // Web mode - use sessionStorage (memory-only)
  return new WebSecureStorage();
}

// Singleton instance
// export const secureStorage = createSecureStorage();

// Utility functions for sensitive data
export const SecureStorageKeys = {
  AUTH_STATE: "ive-auth-state-secure",
  AUTH_CONFIG: "ive-auth-config-secure",
  AUTH_UI_MODE: "ive-auth-ui-mode-secure",
  ACCESS_TOKEN: "ive-access-token-secure",
  REFRESH_TOKEN: "ive-refresh-token-secure",
  PKCE_VERIFIER: "ive-pkce-verifier-secure",
  PARTICIPANT_ID: "pineseed-participant-id-secure",
  DEVICE_FINGERPRINT: "pineseed-device-fingerprint-secure",
} as const;

// Helper functions for common operations
export async function storeAuthTokens(
  secureStorage: SecureStorageInterface,
  accessToken: string,
  refreshToken?: string,
): Promise<void> {
  await secureStorage.setItem(SecureStorageKeys.ACCESS_TOKEN, accessToken);
  if (refreshToken) {
    await secureStorage.setItem(SecureStorageKeys.REFRESH_TOKEN, refreshToken);
  }
}

export async function getAuthTokens(secureStorage: SecureStorageInterface): Promise<{
  accessToken: string | null;
  refreshToken: string | null;
}> {
  const [accessToken, refreshToken] = await Promise.all([
    secureStorage.getItem(SecureStorageKeys.ACCESS_TOKEN),
    secureStorage.getItem(SecureStorageKeys.REFRESH_TOKEN),
  ]);
  return { accessToken, refreshToken };
}

export async function clearAuthTokens(secureStorage: SecureStorageInterface): Promise<void> {
  await Promise.all([
    secureStorage.removeItem(SecureStorageKeys.ACCESS_TOKEN),
    secureStorage.removeItem(SecureStorageKeys.REFRESH_TOKEN),
    secureStorage.removeItem(SecureStorageKeys.AUTH_STATE),
  ]);
}

export async function storePKCEVerifier(secureStorage: SecureStorageInterface, verifier: string): Promise<void> {
  await secureStorage.setItem(SecureStorageKeys.PKCE_VERIFIER, verifier);
}

export async function getPKCEVerifier(secureStorage: SecureStorageInterface): Promise<string | null> {
  return await secureStorage.getItem(SecureStorageKeys.PKCE_VERIFIER);
}

export async function clearPKCEVerifier(secureStorage: SecureStorageInterface): Promise<void> {
  await secureStorage.removeItem(SecureStorageKeys.PKCE_VERIFIER);
}
